import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { ConnectOnboarding } from '@/components/stripe/ConnectOnboarding';
import { ConnectAccountStatus } from '@/components/stripe/ConnectAccountStatus';
import { PayoutManagement } from '@/components/dashboard/PayoutManagement';
import { supabase } from '@/integrations/supabase/client';
import { 
  Store, 
  DollarSign, 
  TrendingUp, 
  Users, 
  Package,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

interface StripeAccount {
  id: string;
  stripe_account_id: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  capabilities_status: any;
  requirements: any;
}

interface SellerStats {
  totalRevenue: number;
  totalSequences: number;
  totalDownloads: number;
  pendingPayouts: number;
}

export default function SellerDashboard() {
  const { user, isLoading: authLoading, validateSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stripeAccount, setStripeAccount] = useState<StripeAccount | null>(null);
  const [sellerStats, setSellerStats] = useState<SellerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionValidated, setSessionValidated] = useState(false);
  const [isStripeReturn, setIsStripeReturn] = useState(false);

  useEffect(() => {
    const checkAuthAndFetchData = async () => {
      // Check if this is a return from Stripe Connect
      const stripeReturn = sessionStorage.getItem('stripe_connect_return');
      const stripeUserId = sessionStorage.getItem('stripe_connect_user_id');
      const stripeTimestamp = sessionStorage.getItem('stripe_connect_timestamp');
      
      // If returning from Stripe and it was recent (within 10 minutes)
      if (stripeReturn && stripeUserId && stripeTimestamp) {
        const timeElapsed = Date.now() - parseInt(stripeTimestamp);
        if (timeElapsed < 600000) { // 10 minutes
          setIsStripeReturn(true);
          console.log('Detected return from Stripe Connect, attempting session restoration...');
        }
      }

      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      // If no user after auth has loaded, validate session first
      if (!user && !sessionValidated) {
        setSessionValidated(true);
        console.log('No user found, validating session...');
        const isValid = await validateSession();
        
        // If session validation fails, check if this was a Stripe return
        if (!isValid) {
          if (isStripeReturn && stripeUserId) {
            console.log('Session validation failed but this is a Stripe return, giving more time...');
            // Give it one more chance - sometimes the session takes time to restore
            setTimeout(async () => {
              const retryValid = await validateSession();
              if (!retryValid) {
                console.log('Session restoration failed, redirecting to auth with return URL');
                sessionStorage.setItem('auth_redirect', '/seller-dashboard');
                // Clear stripe return data to avoid loops
                sessionStorage.removeItem('stripe_connect_return');
                sessionStorage.removeItem('stripe_connect_user_id');
                sessionStorage.removeItem('stripe_connect_timestamp');
                navigate('/auth');
              }
            }, 2000);
            return;
          }
          
          // Store the intended destination for after login
          sessionStorage.setItem('auth_redirect', '/seller-dashboard');
          navigate('/auth');
          return;
        }
      } else if (!user && !isStripeReturn) {
        // No user and session already validated, redirect to auth (unless this is a Stripe return)
        sessionStorage.setItem('auth_redirect', '/seller-dashboard');
        navigate('/auth');
        return;
      }
      
      // User exists, fetch seller data
      if (user) {
        // Clear stripe return data once we have a valid user
        if (isStripeReturn) {
          sessionStorage.removeItem('stripe_connect_return');
          sessionStorage.removeItem('stripe_connect_user_id');
          sessionStorage.removeItem('stripe_connect_timestamp');
          setIsStripeReturn(false);
        }
        fetchSellerData();
      }
    };

    checkAuthAndFetchData();
  }, [user, authLoading, navigate, validateSession, sessionValidated, isStripeReturn]);

  // Handle Stripe Connect return
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const onboardingComplete = searchParams.get('onboarding') === 'complete';
    const accountUpdated = searchParams.get('updated') === 'true';
    const stripeRefresh = searchParams.get('stripe_refresh') === 'true';
    
    // Handle Stripe refresh (back button from Stripe)
    if (stripeRefresh) {
      console.log('Handling Stripe refresh (back button), ensuring session is valid...');
      // Clear the refresh parameter immediately
      navigate('/seller-dashboard', { replace: true });
      
      // Validate session in case it was lost during external redirect
      if (!user && !authLoading) {
        validateSession().then(isValid => {
          if (!isValid) {
            console.log('Session lost during Stripe refresh, redirecting to auth');
            sessionStorage.setItem('auth_redirect', '/seller-dashboard');
            navigate('/auth');
          }
        });
      }
      return;
    }
    
    if (onboardingComplete || accountUpdated) {
      // Ensure user session is valid before showing success message
      const handleStripeReturn = async () => {
        console.log('Handling Stripe Connect return...');
        
        // If we don't have a user yet, wait a bit for session restoration
        if (!user && !authLoading) {
          console.log('No user on Stripe return, attempting session validation...');
          const isValid = await validateSession();
          if (!isValid) {
            console.log('Session validation failed on Stripe return, redirecting to auth');
            sessionStorage.setItem('auth_redirect', '/seller-dashboard');
            navigate('/auth');
            return;
          }
        }
        
        // Show success message
        if (onboardingComplete) {
          toast.success('Stripe Connect setup completed!', {
            description: 'You can now start selling sequences and receiving payments.'
          });
        } else if (accountUpdated) {
          toast.success('Stripe account updated successfully!');
        }
        
        // Refresh seller data to get updated Stripe account info
        if (user) {
          await fetchSellerData();
        }
        
        // Clean up the URL
        navigate('/seller-dashboard', { replace: true });
      };
      
      handleStripeReturn();
    }
  }, [location, navigate, user, authLoading, validateSession]);

  const fetchSellerData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Fetch Stripe account info
      const { data: accountData, error: accountError } = await supabase
        .from('stripe_accounts')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (accountError && accountError.code !== 'PGRST116') {
        console.error('Error fetching Stripe account:', accountError);
      } else if (accountData) {
        setStripeAccount(accountData);
      }

      // Fetch seller statistics
      const { data: sequencesData, error: sequencesError } = await supabase
        .from('sequences')
        .select('id, price, download_count')
        .eq('user_id', user.id);

      if (sequencesError) {
        console.error('Error fetching sequences:', sequencesError);
      } else if (sequencesData) {
        const totalRevenue = sequencesData.reduce((sum, seq) => {
          return sum + (seq.price * seq.download_count);
        }, 0);

        const totalDownloads = sequencesData.reduce((sum, seq) => {
          return sum + seq.download_count;
        }, 0);

        setSellerStats({
          totalRevenue,
          totalSequences: sequencesData.length,
          totalDownloads,
          pendingPayouts: 0 // This would come from payouts calculation
        });
      }

    } catch (error) {
      console.error('Error fetching seller data:', error);
      toast.error('Failed to load seller dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountCreated = (accountId: string) => {
    fetchSellerData();
    toast.success('Stripe Connect account created successfully!');
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="grow container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const hasStripeAccount = !!stripeAccount?.stripe_account_id;
  const accountComplete = stripeAccount?.charges_enabled && stripeAccount?.payouts_enabled;

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="grow container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Store className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Seller Dashboard</h1>
            <p className="text-muted-foreground">
              Manage your sequences, earnings, and payouts
            </p>
          </div>
        </div>

        {!hasStripeAccount && (
          <Alert className="mb-8">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Complete your seller onboarding to start receiving payments for your sequences.
            </AlertDescription>
          </Alert>
        )}

        {hasStripeAccount && !accountComplete && (
          <Alert className="mb-8">
            <Clock className="h-4 w-4" />
            <AlertDescription>
              Your Stripe account setup is in progress. Complete all requirements to enable payouts.
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${((sellerStats?.totalRevenue || 0) / 100).toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                From {sellerStats?.totalDownloads || 0} downloads
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Sequences</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{sellerStats?.totalSequences || 0}</div>
              <p className="text-xs text-muted-foreground">
                Active listings
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Downloads</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{sellerStats?.totalDownloads || 0}</div>
              <p className="text-xs text-muted-foreground">
                Total downloads
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Account Status</CardTitle>
              {accountComplete ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <AlertCircle className="h-4 w-4 text-orange-500" />
              )}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <Badge variant={accountComplete ? "default" : "secondary"}>
                  {accountComplete ? "Active" : hasStripeAccount ? "Pending" : "Setup Required"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Stripe Connect status
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="account" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="account">Account Setup</TabsTrigger>
            <TabsTrigger value="payouts" disabled={!accountComplete}>
              Payouts
            </TabsTrigger>
            <TabsTrigger value="analytics" disabled={!hasStripeAccount}>
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="account" className="space-y-6">
            {!hasStripeAccount ? (
              <ConnectOnboarding 
                userId={user.id}
                onAccountCreated={handleAccountCreated}
              />
            ) : (
              <ConnectAccountStatus 
                userId={user.id}
                accountId={stripeAccount.stripe_account_id}
                onStatusUpdate={fetchSellerData}
              />
            )}
          </TabsContent>

          <TabsContent value="payouts" className="space-y-6">
            {accountComplete && stripeAccount ? (
              <PayoutManagement 
                sellerId={stripeAccount.stripe_account_id}
                sellerName={user.email}
              />
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center text-muted-foreground">
                    Complete your account setup to access payout management
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Sales Analytics</CardTitle>
                <CardDescription>
                  Detailed analytics coming soon
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center text-muted-foreground py-8">
                  Advanced analytics dashboard is under development
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}